/**
 * Espacios interiores de un anuncio ("Espacios" en el editor): un salón, un comedor o una
 * sala cubierta que el anfitrión ofrece además del espacio principal, incluido en el precio o
 * como extra con un precio fijo por reserva. Ver docs/indoor-spaces.md.
 *
 * Viven en `publicData.indoorSpaces`, que NO se registra en Console `listingFields` (un campo
 * declarado de tipo array lo anula `sanitizeConfiguredPublicData`). Las fotos son un
 * subconjunto, por uuid, de las fotos del anuncio. El importe del extra se recalcula siempre en
 * el servidor (server/api-util/indoorSpaces.js), nunca se fía del cliente.
 */

export const INDOOR_SPACE_PRICING_INCLUDED = 'included';
export const INDOOR_SPACE_PRICING_EXTRA = 'extra';

export const MAX_INDOOR_SPACES = 5;
export const INDOOR_SPACE_TITLE_MAX_LENGTH = 80;
export const INDOOR_SPACE_DESCRIPTION_MAX_LENGTH = 1000;

/** Prefijo del line item de un espacio extra: `line-item/espacio-interior-<id>`. */
export const INDOOR_SPACE_LINE_ITEM_PREFIX = 'line-item/espacio-interior-';

/**
 * Opciones del campo `extras` de Console que tienen sentido dentro de un espacio interior.
 * Las que el marketplace no tenga simplemente no salen.
 */
export const INDOOR_SPACE_EQUIPMENT_KEYS = [
  'table-chairs',
  'cocina',
  'Refrigerator',
  'Catcher',
  'Microwave',
  'Oven',
  'Cutlery',
  'barra',
  'tv',
  'Speakers',
  'musica',
  'Wifi',
  'pingpong',
  'bano',
];

const ID_PATTERN = /^[a-z0-9]{4,16}$/;

/** Id corto y estable de un espacio: va en el código del line item. */
export const newIndoorSpaceId = () =>
  Math.random()
    .toString(16)
    .slice(2, 10)
    .padEnd(8, '0');

const trimmed = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');

const positiveInt = value => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

const publicDataOf = listingOrPublicData =>
  listingOrPublicData?.attributes
    ? listingOrPublicData.attributes.publicData || {}
    : listingOrPublicData || {};

/**
 * Un espacio guardado, limpio; `null` si no sirve (sin id válido o sin título). Un espacio
 * "extra" sin un precio positivo se trata como incluido: no hay nada que cobrar.
 */
export const normalizeIndoorSpace = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const id = typeof raw.id === 'string' ? raw.id : '';
  const title = trimmed(raw.title, INDOOR_SPACE_TITLE_MAX_LENGTH);
  if (!ID_PATTERN.test(id) || !title) {
    return null;
  }
  const amount = positiveInt(raw.price?.amount);
  const isExtra = raw.pricing === INDOOR_SPACE_PRICING_EXTRA && amount != null;
  return {
    id,
    title,
    description: trimmed(raw.description, INDOOR_SPACE_DESCRIPTION_MAX_LENGTH),
    capacity: positiveInt(raw.capacity),
    pricing: isExtra ? INDOOR_SPACE_PRICING_EXTRA : INDOOR_SPACE_PRICING_INCLUDED,
    price: isExtra ? { amount, currency: raw.price.currency || 'EUR' } : null,
    imageIds: Array.isArray(raw.imageIds)
      ? raw.imageIds.filter(uuid => typeof uuid === 'string' && uuid)
      : [],
    equipment: Array.isArray(raw.equipment)
      ? raw.equipment.filter(key => INDOOR_SPACE_EQUIPMENT_KEYS.includes(key))
      : [],
  };
};

/**
 * Espacios interiores del anuncio, limpios y sin duplicados de id.
 *
 * @param {Object} listingOrPublicData el anuncio o directamente su `publicData`
 * @returns {Array}
 */
export const indoorSpacesOf = listingOrPublicData => {
  const raw = publicDataOf(listingOrPublicData).indoorSpaces;
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set();
  return raw.map(normalizeIndoorSpace).filter(space => {
    if (!space || seen.has(space.id)) {
      return false;
    }
    seen.add(space.id);
    return true;
  });
};

/** Espacios que se pueden añadir a una reserva pagando aparte. */
export const extraIndoorSpacesOf = listingOrPublicData =>
  indoorSpacesOf(listingOrPublicData).filter(s => s.pricing === INDOOR_SPACE_PRICING_EXTRA);

/**
 * El espacio que representa al anuncio en el buscador (etiqueta, precio y fotos de la tarjeta
 * con el chip "Con espacio interior"): el más barato. Uno incluido si lo hay (no cuesta nada);
 * si no, el extra de menor precio. Con empate, el primero de la lista.
 *
 * @returns {Object|null}
 */
export const featuredIndoorSpace = listingOrPublicData => {
  const spaces = indoorSpacesOf(listingOrPublicData);
  const included = spaces.find(s => s.pricing !== INDOOR_SPACE_PRICING_EXTRA);
  if (included) {
    return included;
  }
  return spaces.reduce(
    (cheapest, s) => (!cheapest || s.price.amount < cheapest.price.amount ? s : cheapest),
    null
  );
};

/** Param del enlace de la tarjeta a la ficha: el espacio llega ya añadido a la reserva. */
export const INDOOR_SPACE_QUERY_PARAM = 'espacioInterior';

/**
 * Lo que guarda el panel Espacios: la lista entera (Flex reemplaza un valor anidado de
 * `publicData` entero, no lo fusiona) y `hasIndoorSpace`, derivado, que alimenta el chip
 * "Con espacio interior" del buscador. Sin espacios, los dos a null (Flex borra la clave).
 *
 * @param {Array} spaces
 * @returns {{ indoorSpaces: Array|null, hasIndoorSpace: true|null }}
 */
export const indoorSpacesPublicData = spaces => {
  const clean = (Array.isArray(spaces) ? spaces : [])
    .map(normalizeIndoorSpace)
    .filter(Boolean)
    .slice(0, MAX_INDOOR_SPACES);
  return clean.length > 0
    ? { indoorSpaces: clean, hasIndoorSpace: true }
    : { indoorSpaces: null, hasIndoorSpace: null };
};

/**
 * Al guardar las fotos del anuncio: quita de cada espacio las fotos que ya no existen. Devuelve
 * `null` si no hay que tocar nada.
 */
export const indoorSpacesWithoutRemovedImages = (listingOrPublicData, remainingImageUuids) => {
  const spaces = indoorSpacesOf(listingOrPublicData);
  const remaining = new Set(remainingImageUuids);
  let changed = false;
  const next = spaces.map(space => {
    const imageIds = space.imageIds.filter(uuid => remaining.has(uuid));
    if (imageIds.length !== space.imageIds.length) {
      changed = true;
      return { ...space, imageIds };
    }
    return space;
  });
  return changed ? next : null;
};

export const indoorSpaceLineItemCode = id => `${INDOOR_SPACE_LINE_ITEM_PREFIX}${id}`;

export const isIndoorSpaceLineItem = code =>
  typeof code === 'string' && code.startsWith(INDOOR_SPACE_LINE_ITEM_PREFIX);

/**
 * Título del espacio de un line item, leído del anuncio; `null` si el espacio ya no existe
 * (el anfitrión lo borró después de la reserva).
 */
export const indoorSpaceTitleForLineItem = (listing, code) => {
  if (!isIndoorSpaceLineItem(code)) {
    return null;
  }
  const id = code.slice(INDOOR_SPACE_LINE_ITEM_PREFIX.length);
  return indoorSpacesOf(listing).find(s => s.id === id)?.title || null;
};
